import { useEffect, useState } from "react"

export type LogoValidationState = "pending" | "valid" | "invalid"

/**
 * Hook to check if a logo URL is valid (file exists).
 * Starts as `pending` so callers can avoid flashing the embedded fallback
 * before the async fetch completes.
 */
export function useLogoValidation(logoUrl: string): LogoValidationState {
  const [state, setState] = useState<LogoValidationState>("pending")

  useEffect(() => {
    setState("pending")

    const checkLogo = async () => {
      try {
        // Fetch and validate response type. Unknown asset paths can return SPA HTML with 200.
        const response = await fetch(logoUrl)
        if (!response.ok) {
          setState("invalid")
          return
        }

        const contentType = (response.headers.get("content-type") ?? "").toLowerCase()
        if (contentType.includes("text/html")) {
          setState("invalid")
          return
        }

        if (contentType.startsWith("image/")) {
          setState("valid")
          return
        }

        // Fallback for servers that omit image content-type but still return SVG.
        const body = await response.text()
        setState(body.trimStart().startsWith("<svg") ? "valid" : "invalid")
      } catch {
        setState("invalid")
      }
    }

    void checkLogo()
  }, [logoUrl])

  return state
}

/** Combine light/dark validation — both must be valid to use external logos. */
export function resolveBothLogosValidation(
  light: LogoValidationState,
  dark: LogoValidationState
): LogoValidationState {
  if (light === "pending" || dark === "pending") {
    return "pending"
  }
  if (light === "valid" && dark === "valid") {
    return "valid"
  }
  return "invalid"
}

export function useBothLogosValidation(lightUrl: string, darkUrl: string): LogoValidationState {
  const light = useLogoValidation(lightUrl)
  const dark = useLogoValidation(darkUrl)
  return resolveBothLogosValidation(light, dark)
}
